/**
 * The event log a player has already seen, remembered for this tab.
 *
 * The log is composed on the client, one line at a time, from the socket
 * events as they arrive — nothing on the server records it. So a refresh used
 * to hand you an empty panel in the middle of a game you had been watching for
 * ten rounds. Keeping it here is what makes the refresh invisible, and it sits
 * beside playerId and roomPassword because it is per-tab for the same reason
 * they are: the tab is the player.
 *
 * This restores your own history, not the room's. A new tab, and a player who
 * joins late, still start empty — those lines were never anywhere to be read.
 * The room keeping its own log is a different, larger change.
 */
const MAX_ENTRIES = 50;

const key = gameId => `robotrons:log:${gameId}`;

/**
 * An entry as `addLog` writes it: newest first, `{ key, params?, time }`.
 *
 * A `{ text, time }` entry is the pre-catalog shape, and is still accepted on
 * read. A player refreshing mid-game across the deploy that introduced
 * translations would otherwise open the empty panel this file exists to
 * prevent — so those lines stay, in the language they were written in, which
 * is the only language they can be in.
 */
function isEntry(value) {
  if (!value || typeof value !== 'object') return false;
  if (typeof value.time !== 'number') return false;
  return typeof value.key === 'string' || typeof value.text === 'string';
}

export function loadLog(gameId) {
  try {
    const stored = sessionStorage.getItem(key(gameId));
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    // Anything else under this key is somebody else's data or a half-written
    // value, and an empty log is always a valid answer.
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isEntry).slice(0, MAX_ENTRIES);
  } catch {
    return [];
  }
}

export function saveLog(gameId, entries) {
  try {
    sessionStorage.setItem(key(gameId), JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch {
    // Blocked site data, or a full quota. Losing the log across a refresh is
    // the thing this exists to avoid, not a thing worth failing a render for.
  }
}
